import { doc, getDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface PointsTransaction {
  type: 'withdrawal' | 'purchase' | 'return' | 'approved' | 'redemption';
  amount: number;
  timestamp: Timestamp;
  requestId?: string;
}

export const syncPointsAfterPurchase = async (userId: string, email: string) => {
  const userRef = doc(db, 'users', userId);

  try {
    // Récupérer d'abord les données actuelles
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return 0;

    // Vérifier si une synchronisation est déjà en cours
    const lastSync = userDoc.data()?.points?.lastSync;
    const now = Timestamp.now();
    if (lastSync && now.seconds - lastSync.seconds < 10) {
      console.log('Synchronisation ignorée - trop récente');
      return userDoc.data()?.points?.available || 0;
    }

    // Récupérer les licences
    const response = await fetch(
      'https://services.shopiweb.fr/api/licenses/get_by_email/premium',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email }).toString()
      }
    );

    const data = await response.json();
    const premiumLicenses = data.user?.nbAuthorizedLicenseType?.premium || 0;
    const basePoints = premiumLicenses * 399;

    await runTransaction(db, async (transaction) => {
      const freshDoc = await transaction.get(userRef);
      const currentData = freshDoc.data();
      const currentHistory = currentData?.points?.history || [];

      // Calculer le solde réel basé sur l'historique
      const historyBalance = currentHistory.reduce((total: number, tx: PointsTransaction) => {
        if (tx.type === 'approved') return total; // Ignorer les approbations
        return total + tx.amount;
      }, 0);

      // Le solde final doit être basePoints + historyBalance
      const finalBalance = basePoints + historyBalance;

      transaction.update(userRef, {
        'points.available': finalBalance,
        'points.lastUpdate': now,
        'points.lastSync': now,
        'points.basePoints': basePoints // Stocker les points de base
      });
    });

    return basePoints;
  } catch (error) {
    console.error('Error syncing points:', error);
    // Ne doit jamais faire échouer une connexion réussie
    return 0;
  }
};

export const forcePointsRefresh = async (userId: string): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  try {
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return;

    const email = userDoc.data()?.email;
    if (!email) return;

    await syncPointsAfterPurchase(userId, email);
  } catch (error) {
    console.error('Error refreshing points:', error);
    throw error;
  }
};